import { Box, Container, Grid, Stack, Typography } from '@mui/material'
import { messageBus, useOperationsStore } from '@operations/AppProvider'
import { VesselKPIUIStore } from '@operations/features/vesselVisitDashboard/stores/vessel-kpi.ui-store'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { authStore, formatDateTime, useAsyncFetch } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { VesselVisitKPIStats } from '../../models/vessel-kpi'
import { VesselVisitKPIUIStore } from '../../stores/vessel-visit-kpi.ui-store'
import { PerformanceCard } from './PerformanceCard'
import { StoppagesCard } from './StoppagesCard'
import { VesselVisitKPISummary } from './VesselVisitKPISummary'
import { VesselVisitKPITable } from './VesselVisitKPITable'

interface Props {
  divId?: string
  visitId: number
  vesselVisitKPIUIStore: VesselVisitKPIUIStore
  isVisitCompleted: boolean
  vesselVisitKPIStats: VesselVisitKPIStats
  isDownloadingPDF?: boolean
  visitOverviewComponent: (minutesLeft: number) => React.ReactNode
}
export const VesselVisitKPIContainer = observer(
  ({
    visitId,
    vesselVisitKPIUIStore,
    isVisitCompleted,
    vesselVisitKPIStats,
    visitOverviewComponent,
    divId,
    isDownloadingPDF,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    const { appStore, isCraneSplitOutdatedUIStore } = useOperationsStore()

    const vesselKPIUIStore = useMemo(() => new VesselKPIUIStore(messageBus, appStore), [appStore])

    useAsyncFetch(
      async () => {
        if (visitId) {
          await vesselKPIUIStore.getVisitPerformance(visitId)
          vesselKPIUIStore.setVesselVisitId(visitId)
          await isCraneSplitOutdatedUIStore.load(visitId)
        }
      },
      [isCraneSplitOutdatedUIStore, vesselKPIUIStore, visitId],
      {
        cleanUp: () => {
          isCraneSplitOutdatedUIStore.setVesselVisitId()
        },
      },
    )

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
        }}
        id={divId}
      >
        <Container sx={{ paddingY: theme.customSpacing.m }}>
          <Stack gap={theme.customSpacing.l}>
            {isDownloadingPDF && (
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='h2'>
                  {t('terminalPerformanceReport', 'Terminal Performance Report')}
                </Typography>
                <Stack>
                  <Typography variant='body1'>
                    {authStore.tenantName}, {authStore.tenantId}
                  </Typography>
                  <Typography variant='body1'>{formatDateTime(dayjs())}</Typography>
                </Stack>
              </Stack>
            )}
            {visitOverviewComponent(vesselKPIUIStore.visitPerformance?.minutesLeft ?? 0)}
            <VesselVisitKPISummary
              visitId={visitId}
              isCompleted={isVisitCompleted}
              vesselVisitKPIStats={vesselVisitKPIStats}
              vesselKPIUIStore={vesselKPIUIStore}
            />
            <VesselVisitKPITable
              store={vesselVisitKPIUIStore}
              enablePagination={!isDownloadingPDF}
            />
            {isVisitCompleted && (
              <Grid container size={12} spacing={theme.customSpacing.m}>
                <Grid size={4}>
                  <StoppagesCard stoppagesDetail={vesselKPIUIStore.visitPerformance?.stoppages} />
                </Grid>
                <Grid size={8}>
                  <PerformanceCard
                    terminalPerformance={vesselKPIUIStore.visitPerformance?.terminal}
                  />
                </Grid>
              </Grid>
            )}
          </Stack>
        </Container>
      </Box>
    )
  },
)
